import React, { useCallback, useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { Minus, Plus, Trash2, Check, Loader2, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { getServiceIconAndColor } from './AddAirconItems'
import { airconItemsForCountry, userItemSelectionList } from '../../../../utils/items-selection'
import { localiseTerms } from '../../../../utils/markets'
import { formatMoney } from '../../../../utils/format-money'
import { itemLabel, shortItemLabel, shortMountLabel } from '../../../../utils/market-labels'

// Presentation pieces shared by the two aircon item sheets: the new-booking
// sheet (AirconItemSelection) and the update-an-existing-request sheet
// (home/.../UpdateServiceItems). No pricing or request logic lives here, so
// both sheets look and behave the same while keeping their own cost flows.
//
// `market` (optional, UAE / Malaysia only) localises the labels and prices the
// line items in the market's currency. Item names themselves are backend keys
// and are never changed - only how they are displayed.

export const MAX_UNITS = 20
export const MOUNT_TYPES = ["Wall", "Ceiling", "Ducted"]
// Customer-facing services: repair first, then the standard list.
export const SERVICE_OPTIONS = ["Aircon Repair (est.)", ...userItemSelectionList["Aircon Services"]]
// The options a market's pickers show (Chemical Hydro Wash is Singapore only)
export const serviceOptionsFor = (market) => airconItemsForCountry(SERVICE_OPTIONS, market?.countryCd)
export const displayName = (itemName, market) =>
    itemLabel(itemName, market) ?? localiseTerms(itemName === "Aircon Repair (est.)" ? "Aircon Repair" : itemName, market)
const linePrice = (cost, market) =>
    market && market.countryCd !== 'sg' ? formatMoney(cost || 0, market.countryCd) : `$${Number(cost || 0).toFixed(2)}`

// Accessible segmented control: a row of 40px pill buttons.
export const SegmentedControl = ({ options, value, onChange, ariaLabel, size = "md" }) => (
    <div role="group" aria-label={ariaLabel} className="inline-flex p-1 bg-gray-100 rounded-xl gap-1">
        {options.map((opt) => {
            const isActive = opt.value === value
            return (
                <button
                    key={opt.value}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => onChange(opt.value)}
                    className={`${size === "sm" ? 'min-h-9 px-2 text-xs' : 'min-h-10 px-4 text-sm'} rounded-lg font-semibold transition-colors ${
                        isActive ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                    {opt.label}
                </button>
            )
        })}
    </div>
);

export const SectionTitle = ({ step, children }) => (
    <h3 className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-2">
        <span className="w-5 h-5 flex items-center justify-center rounded-full bg-orange-100 text-orange-600 text-[11px]">{step}</span>
        {children}
    </h3>
);

// Short labels for the collapsed chip row, where width is tight.
export const SHORT_NAMES = {
    "Aircon Repair (est.)": "Repair",
    "General Servicing": "General",
    "Gas Refill Full Tank": "Gas Full",
    "Gas Refill Half Tank": "Gas Half",
    "Chemical Wash": "Chem Wash",
    "Chemical Hydro Wash": "Hydro Wash",
    "Chemical Overhaul": "Overhaul",
    "Condenser Cleaning": "Condenser",
    "Condenser Chem. Wash": "Cond. Chem",
}

export const AddedBadge = ({ count }) => (
    <span className="absolute -top-1.5 -right-1.5 flex items-center gap-0.5 bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow">
        <Check className="w-3 h-3" />{count > 1 ? count : ''}
    </span>
)

export const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// "2025-09-25" -> local Date. new Date("2025-09-25") parses as UTC midnight,
// which is the previous day west of UTC.
export const parseLocalDate = (dateStr) => {
    const [y, m, d] = dateStr.split('-').map(Number)
    return new Date(y, m - 1, d)
}

// "Fri 25 Sep"
export const shortDateLabel = (dateStr) => {
    const d = parseLocalDate(dateStr)
    return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`
}

// "13:00" -> "1pm", "13:30" -> "1:30pm"
export const shortTime = (t) => {
    const [h, m] = t.split(':').map(Number)
    const hour = h % 12 === 0 ? 12 : h % 12
    return `${hour}${m ? ':' + String(m).padStart(2, '0') : ''}${h < 12 ? 'am' : 'pm'}`
}

// Horizontal row that makes its overflow obvious. A plain swipe row with a
// faint fade was easy to miss on a phone, so: a chevron button and fade appear
// on whichever side has more content, and `nudge` does a one-off peek scroll
// when the row first appears. Expects to sit inside a px-3 parent.
export const ScrollRow = ({ children, itemLabel, nudge = false, rowRef, className = '' }) => {
    const ownRef = useRef(null)
    const ref = rowRef || ownRef
    const [edges, setEdges] = useState({ left: false, right: false })

    const measure = useCallback(() => {
        const el = ref.current
        if (!el) return
        const left = el.scrollLeft > 4
        const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 4
        setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }))
    }, [ref])

    // Children change the scroll width, so re-measure after every render.
    // measure() only sets state when an edge actually flips, so this can't loop.
    useEffect(() => { measure() })

    useEffect(() => {
        const el = ref.current
        if (!el || typeof ResizeObserver === 'undefined') return
        const observer = new ResizeObserver(measure)
        observer.observe(el)
        return () => observer.disconnect()
    }, [ref, measure])

    useEffect(() => {
        const el = ref.current
        if (!nudge || !el || el.scrollWidth <= el.clientWidth) return
        const peek = setTimeout(() => el.scrollTo({ left: 64, behavior: 'smooth' }), 450)
        const back = setTimeout(() => el.scrollTo({ left: 0, behavior: 'smooth' }), 1100)
        return () => { clearTimeout(peek); clearTimeout(back) }
    }, [nudge, ref])

    const page = (dir) => {
        const el = ref.current
        el?.scrollBy({ left: dir * el.clientWidth * 0.7, behavior: 'smooth' })
    }

    const edgeButton = "absolute top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-white border border-gray-200 shadow-md text-gray-700 active:scale-95 transition-transform"
    return (
        <div className="relative -mx-3">
            <div ref={ref} onScroll={measure} className={`relative flex gap-2 overflow-x-auto scrollbar-hide px-3 ${className}`}>
                {children}
            </div>
            {edges.left && (
                <>
                    <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-linear-to-r from-white via-white/80 to-transparent" />
                    <button type="button" onClick={() => page(-1)} aria-label={`Show previous ${itemLabel}`} className={`${edgeButton} left-1.5`}>
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                </>
            )}
            {edges.right && (
                <>
                    <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-linear-to-l from-white via-white/80 to-transparent" />
                    <button type="button" onClick={() => page(1)} aria-label={`Show more ${itemLabel}`} className={`${edgeButton} right-1.5`}>
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </>
            )}
        </div>
    )
}

// Service tile in the "What do you need?" grid, shown until the first service
// is added. Compact (about 44px tall) so the grid fits a 360x740 phone with room
// to spare. Tapping always adds a row, so a customer can add the same service
// twice with different mount types.
export const ServiceTile = ({ itemName, addedCount, isAdding, disabled, onAdd, market }) => {
    const { icon: Icon, bgColor, iconColor } = getServiceIconAndColor(itemName);
    const isAdded = addedCount > 0
    return (
        <button
            type="button"
            onClick={() => onAdd(itemName)}
            disabled={disabled}
            aria-label={isAdded ? `Add another ${displayName(itemName, market)}` : `Add ${displayName(itemName, market)}`}
            className={`relative flex items-center gap-2 min-h-11 px-2 py-1.5 rounded-xl border text-left transition-all active:scale-[0.98] disabled:opacity-60 ${
                isAdded ? 'border-orange-400 bg-orange-50/60 ring-1 ring-orange-200' : 'border-gray-200 bg-white hover:border-orange-300'
            }`}
        >
            <span className={`w-7 h-7 flex items-center justify-center rounded-lg shrink-0 ${bgColor} ${iconColor}`}>
                {isAdding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
            </span>
            <span className="text-[13px] font-medium text-gray-800 leading-tight">{displayName(itemName, market)}</span>
            {isAdded && <AddedBadge count={addedCount} />}
        </button>
    );
};

// Chip in the one-row picker shown once a service is added, so the list and
// the schedule get the vertical space.
export const ServiceChip = ({ itemName, addedCount, isAdding, disabled, onAdd, market }) => {
    const { icon: Icon, iconColor } = getServiceIconAndColor(itemName);
    const isAdded = addedCount > 0
    return (
        <button
            type="button"
            onClick={() => onAdd(itemName)}
            disabled={disabled}
            aria-label={isAdded ? `Add another ${displayName(itemName, market)}` : `Add ${displayName(itemName, market)}`}
            className={`relative shrink-0 flex items-center gap-1.5 min-h-10 pl-2.5 pr-3 rounded-full border text-[13px] font-medium whitespace-nowrap transition-all active:scale-[0.97] disabled:opacity-60 ${
                isAdded ? 'border-orange-400 bg-orange-50 text-gray-900' : 'border-gray-200 bg-white text-gray-700'
            }`}
        >
            {isAdding ? <Loader2 className={`w-3.5 h-3.5 animate-spin ${iconColor}`} /> : <Plus className="w-3.5 h-3.5 text-orange-500" />}
            <Icon className={`w-3.5 h-3.5 ${iconColor}`} aria-hidden="true" />
            {shortItemLabel(itemName, market) || SHORT_NAMES[itemName] || displayName(itemName, market)}
            {isAdded && <AddedBadge count={addedCount} />}
        </button>
    );
};

// One selected service in a single row (~64px): icon, name, then mount type
// and line price underneath, stepper on the right. At 1 unit the − button
// becomes a bin, so there is no separate delete button taking up space.
export const SelectedServiceCard = ({ item, index, isHighlighted, isUpdating, disabled, onUnitsChange, onMountTypeChange, market }) => {
    const { icon: Icon, bgColor, iconColor } = getServiceIconAndColor(item.itemName);
    const hasMountType = MOUNT_TYPES.includes(item.mountType)
    const name = displayName(item.itemName, market)
    const isLastUnit = item.unitCount === 1
    return (
        <motion.div
            id={`aircon-item-${index}`}
            className={`flex items-center gap-2 px-2.5 py-2 bg-white border rounded-2xl scroll-my-2 transition-shadow duration-500 ${
                isHighlighted ? 'border-orange-400 ring-2 ring-orange-200' : 'border-gray-200'
            }`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, x: 60 }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            layout
        >
            <span className={`w-7 h-7 flex items-center justify-center rounded-lg shrink-0 ${bgColor} ${iconColor}`}>
                <Icon className="w-3.5 h-3.5" />
            </span>
            <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-gray-900 truncate leading-tight">{name}</div>
                <div className="flex items-center gap-2 mt-1">
                    {hasMountType && (
                        <div className="relative shrink-0">
                            <select
                                value={item.mountType}
                                disabled={disabled}
                                onChange={(e) => onMountTypeChange({ index, mountType: e.target.value })}
                                aria-label={`Mount type for ${name}`}
                                className="appearance-none h-7 pl-2 pr-6 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 disabled:opacity-50"
                            >
                                {MOUNT_TYPES.map((m) => <option key={m} value={m}>{shortMountLabel(m, market)}</option>)}
                            </select>
                            <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
                        </div>
                    )}
                    <span className="text-[13px] font-bold text-gray-900 whitespace-nowrap tabular-nums">
                        {isUpdating
                            ? <Loader2 className="w-4 h-4 animate-spin text-orange-500" aria-label="Updating price" />
                            : linePrice(item.itemCost, market)}
                    </span>
                </div>
            </div>
            <div className="flex items-center gap-1 shrink-0" role="group" aria-label="Number of units">
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onUnitsChange({ index, unitCount: item.unitCount - 1 })}
                    aria-label={isLastUnit ? `Remove ${name}` : "Decrease units"}
                    className={`w-9 h-9 flex items-center justify-center bg-white border rounded-full transition-colors disabled:opacity-50 ${
                        isLastUnit ? 'text-gray-400 border-gray-200 hover:text-red-500 hover:border-red-300' : 'text-gray-600 border-gray-300 hover:border-orange-400 hover:text-orange-500'
                    }`}
                >
                    {isLastUnit ? <Trash2 className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
                </button>
                <span className="min-w-6 text-center text-base font-bold text-gray-900 tabular-nums" aria-live="polite" aria-label={`${item.unitCount} ${isLastUnit ? 'unit' : 'units'}`}>
                    {item.unitCount}
                </span>
                <button
                    type="button"
                    disabled={disabled || item.unitCount >= MAX_UNITS}
                    onClick={() => onUnitsChange({ index, unitCount: item.unitCount + 1 })}
                    aria-label="Increase units"
                    className="w-9 h-9 flex items-center justify-center text-orange-600 bg-orange-50 border border-orange-300 rounded-full hover:bg-orange-100 transition-colors disabled:opacity-40"
                >
                    <Plus className="w-4 h-4" />
                </button>
            </div>
        </motion.div>
    );
};
